import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { BehaviorSubject } from 'rxjs';
import { initializeApp, getApp, getApps, type FirebaseApp, type FirebaseOptions } from 'firebase/app';
import { getAnalytics, isSupported as isAnalyticsSupported, type Analytics } from 'firebase/analytics';
import {
  Auth,
  GoogleAuthProvider,
  User,
  createUserWithEmailAndPassword,
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  updateProfile,
} from 'firebase/auth';
import { Database, get, getDatabase, ref, remove, set, update } from 'firebase/database';
import { CompositionMem, SongCompositionMem } from '../interfaceTypes';
import TaalStructure from '../TaalStructure.json';

const RACHANA_FIREBASE_CONFIG: FirebaseOptions = {
  apiKey: 'AIzaSyDakw8TyGR2GEpYRf0r14Z2yuvMqBqMyAw',
  authDomain: 'rachana-acc40.firebaseapp.com',
  databaseURL: 'https://rachana-acc40-default-rtdb.firebaseio.com',
  projectId: 'rachana-acc40',
  storageBucket: 'rachana-acc40.firebasestorage.app',
  messagingSenderId: '1088416344684',
  appId: '1:1088416344684:web:b3482058a799ef0d740649',
  measurementId: 'G-6YYH8Q64BK'
};

export type CompositionVisibility = 'private' | 'public' | 'unlisted' | 'shared';

export interface CompositionCommentRecord {
  authorUid: string;
  authorName: string;
  text: string;
  createdAt: string;
}

export interface TaalSnapshot {
  name: string;
  beats: number;
  bols: string[];
  vibhags: Array<{
    label: string;
    beats: number[];
  }>;
}

export interface UserCompositionRecord {
  id: string;
  shareId: string;
  ownerUid: string;
  title: string;
  titleLower: string;
  updatedAt: string;
  visibility: CompositionVisibility;
  pendingShareEmails: Record<string, true>;
  pendingSharePhones: Record<string, true>;
  aclUid: Record<string, true>;
  taalData: TaalSnapshot | null;
  comments: Record<string, CompositionCommentRecord>;
  likes: Record<string, true>;
  composition: CompositionMem;
}

export interface UserSongCompositionRecord {
  id: string;
  ownerUid: string;
  title: string;
  titleLower: string;
  updatedAt: string;
  songComposition: SongCompositionMem;
}

export interface SharedUserRecord {
  uid: string;
  email: string;
  displayName: string;
  createdAt: string;
  updatedAt: string;
}

export interface UserAppRecord {
  exists: boolean;
  createdAt: string;
  updatedAt: string;
}

@Injectable({
  providedIn: 'root'
})
export class UserDataService {
  private readonly appName = 'Rachana';
  private readonly platform = Capacitor.getPlatform();
  private readonly localCachePrefix = `rachana:compositions:${this.platform}`;
  private readonly localSongCachePrefix = `rachana:song-compositions:${this.platform}`;
  private readonly localCacheLastUidKey = `${this.localCachePrefix}:last_uid`;
  private readonly firebaseConfig = RACHANA_FIREBASE_CONFIG;
  private readonly configured = Boolean(
    this.firebaseConfig.apiKey &&
    this.firebaseConfig.projectId &&
    this.firebaseConfig.databaseURL
  );

  private readonly app: FirebaseApp | null = this.configured
    ? (getApps().length ? getApp() : initializeApp(this.firebaseConfig))
    : null;
  private analytics: Analytics | null = null;
  private readonly auth: Auth | null = this.app ? getAuth(this.app) : null;
  private readonly db: Database | null = this.app ? getDatabase(this.app) : null;

  private readonly userSubject = new BehaviorSubject<User | null>(null);
  private authInitialized = false;
  private authReadyResolver: (() => void) | null = null;
  private readonly authReadyPromise = new Promise<void>((resolve) => {
    this.authReadyResolver = resolve;
  });
  readonly user$ = this.userSubject.asObservable();

  constructor() {
    void this.initializeAnalytics();

    if (!this.auth) {
      this.markAuthReady();
      return;
    }

    onAuthStateChanged(this.auth, async (user) => {
      this.userSubject.next(user);
      this.markAuthReady();
      if (user) {
        void this.ensureUserRecord(user).catch((error) => {
          console.error('Failed to sync Firebase user record.', error);
        });
      }
    });
  }

  get isConfigured(): boolean {
    return this.configured;
  }

  get currentUser(): User | null {
    return this.userSubject.value;
  }

  async signIn(email: string, password: string): Promise<void> {
    const auth = this.getAuthOrThrow();
    await signInWithEmailAndPassword(auth, email, password);
  }

  async signUp(email: string, password: string, displayName: string): Promise<void> {
    const auth = this.getAuthOrThrow();
    const credential = await createUserWithEmailAndPassword(auth, email, password);
    if (displayName.trim()) {
      await updateProfile(credential.user, { displayName: displayName.trim() });
    }
    await this.ensureUserRecord(credential.user, displayName.trim());
  }

  async signInWithGoogle(): Promise<void> {
    const auth = this.getAuthOrThrow();
    const provider = new GoogleAuthProvider();
    if (this.platform === 'web') {
      await signInWithPopup(auth, provider);
      return;
    }
    await signInWithRedirect(auth, provider);
  }

  async signOutUser(): Promise<void> {
    const auth = this.getAuthOrThrow();
    await signOut(auth);
  }

  async waitForAuthReady(): Promise<void> {
    await this.authReadyPromise;
  }

  async listCompositions(uid: string): Promise<UserCompositionRecord[]> {
    try {
      const compositions = await this.getRemoteCompositions(uid);
      this.saveLocalCompositions(uid, compositions);
      return this.sortCompositions(compositions);
    } catch (error) {
      console.warn('Falling back to local composition cache.', error);
      return this.listLocalCompositions(uid);
    }
  }

  async getComposition(uid: string, compositionId: string): Promise<UserCompositionRecord | null> {
    try {
      const composition = await this.getCompositionRecord(compositionId);
      if (!composition || composition.ownerUid !== uid) {
        return null;
      }
      return this.normalizeCompositionRecord(composition);
    } catch {
      return this.listLocalCompositions(uid).find((composition) => composition.id === compositionId) ?? null;
    }
  }

  async getAccessibleComposition(compositionId: string): Promise<UserCompositionRecord | null> {
    try {
      const composition = await this.getCompositionRecord(compositionId);
      return composition ? this.normalizeCompositionRecord(composition) : null;
    } catch {
      const uid = this.getLastCachedUid();
      if (!uid) {
        return null;
      }
      return this.listLocalCompositions(uid).find((composition) => composition.id === compositionId) ?? null;
    }
  }

  async getCompositionByShareId(shareId: string): Promise<UserCompositionRecord | null> {
    const db = this.getDatabaseOrThrow();
    const snapshot = await get(ref(db, this.getShareIndexPath(shareId)));
    if (!snapshot.exists()) {
      return null;
    }

    const compositionId = snapshot.val() as string;
    const composition = await this.getCompositionRecord(compositionId);
    return composition ? this.normalizeCompositionRecord(composition) : null;
  }

  listReadonlyCompositions(): UserCompositionRecord[] {
    const uid = this.getLastCachedUid();
    if (!uid) {
      return [];
    }
    return this.listLocalCompositions(uid);
  }

  getReadonlyComposition(compositionId: string): UserCompositionRecord | null {
    return this.listReadonlyCompositions().find((composition) => composition.id === compositionId) ?? null;
  }

  async saveComposition(
    uid: string,
    composition: CompositionMem,
    compositionId?: string | null
  ): Promise<UserCompositionRecord> {
    const db = this.getDatabaseOrThrow();
    const authUser = this.currentUser;
    const userAppRecord = await this.getUserAppRecord(uid);
    const now = new Date().toISOString();
    const hasExistingId = Boolean(compositionId && compositionId.trim());
    const recordId = hasExistingId ? (compositionId as string).trim() : this.createId();
    const existingRecord = hasExistingId ? await this.getCompositionRecord(recordId) : null;
    const shareId = existingRecord?.shareId || composition.shareId || this.createShareId();
    const visibility = this.normalizeVisibility(composition.visibility ?? existingRecord?.visibility);
    const pendingShareEmails = this.normalizeStringMapFromList(
      composition.pendingShareEmails,
      existingRecord?.pendingShareEmails
    );
    const pendingSharePhones = this.normalizeStringMapFromList(
      composition.pendingSharePhones,
      existingRecord?.pendingSharePhones
    );
    const aclUid = this.normalizeTrueMap(existingRecord?.aclUid, composition.aclUid, uid);
    const title = composition.title?.trim() || 'Untitled Composition';
    const compositionToStore: CompositionMem = {
      ...composition,
      shareId,
      visibility,
      pendingShareEmails: Object.keys(pendingShareEmails),
      pendingSharePhones: Object.keys(pendingSharePhones),
      aclUid,
      taalData: this.getTaalSnapshot(composition.taal)
    };
    const nextRecord: UserCompositionRecord = this.normalizeCompositionRecord({
      id: recordId,
      shareId,
      ownerUid: uid,
      title,
      titleLower: title.toLowerCase(),
      updatedAt: now,
      visibility,
      pendingShareEmails,
      pendingSharePhones,
      aclUid,
      taalData: this.getTaalSnapshot(composition.taal),
      comments: existingRecord?.comments ?? {},
      likes: existingRecord?.likes ?? {},
      composition: compositionToStore
    });

    await update(ref(db, this.getSharedUserPath(uid)), {
      uid,
      email: authUser?.uid === uid ? (authUser.email ?? '') : '',
      displayName: authUser?.uid === uid ? (authUser.displayName ?? '') : '',
      updatedAt: now,
      createdAt: now
    });

    await update(ref(db, this.getUserAppPath(uid)), {
      exists: true,
      createdAt: userAppRecord?.createdAt ?? now,
      updatedAt: now
    });

    const firebaseRecord = this.removeUndefinedValues(nextRecord) as UserCompositionRecord;
    await set(ref(db, this.getCompositionRecordPath(recordId)), firebaseRecord);
    await Promise.all([
      set(ref(db, this.getCompositionRefPath(uid, recordId)), true),
      set(ref(db, this.getShareIndexPath(shareId)), recordId),
      this.syncPublicIndex(db, nextRecord)
    ]);

    const compositions = await this.getRemoteCompositions(uid);
    this.saveLocalCompositions(uid, compositions);

    return nextRecord;
  }

  async deleteComposition(uid: string, compositionId: string): Promise<void> {
    const db = this.getDatabaseOrThrow();
    const record = await this.getCompositionRecord(compositionId);

    if (!record || record.ownerUid !== uid) {
      throw new Error('Composition not found.');
    }

    await Promise.all([
      remove(ref(db, this.getCompositionRecordPath(compositionId))),
      remove(ref(db, this.getCompositionRefPath(uid, compositionId))),
      remove(ref(db, this.getShareIndexPath(record.shareId))),
      remove(ref(db, this.getPublicIndexPath(compositionId)))
    ]);

    const compositions = await this.getRemoteCompositions(uid);
    this.saveLocalCompositions(uid, compositions);
  }

  async listSongCompositions(uid: string): Promise<UserSongCompositionRecord[]> {
    try {
      const compositions = await this.getRemoteSongCompositions(uid);
      this.saveLocalSongCompositions(uid, compositions);
      return this.sortSongCompositions(compositions);
    } catch (error) {
      console.warn('Falling back to local song composition cache.', error);
      return this.listLocalSongCompositions(uid);
    }
  }

  async getSongComposition(uid: string, compositionId: string): Promise<UserSongCompositionRecord | null> {
    try {
      const composition = await this.getSongCompositionRecord(compositionId);
      if (!composition || composition.ownerUid !== uid) {
        return null;
      }
      return this.normalizeSongCompositionRecord(composition);
    } catch {
      return this.listLocalSongCompositions(uid).find((composition) => composition.id === compositionId) ?? null;
    }
  }

  async saveSongComposition(
    uid: string,
    songComposition: SongCompositionMem,
    compositionId?: string | null
  ): Promise<UserSongCompositionRecord> {
    const db = this.getDatabaseOrThrow();
    const userAppRecord = await this.getUserAppRecord(uid);
    const now = new Date().toISOString();
    const hasExistingId = Boolean(compositionId && compositionId.trim());
    const recordId = hasExistingId ? (compositionId as string).trim() : this.createSongCompositionId();
    const title = songComposition.name?.trim() || 'Untitled Song Composition';
    const compositionToStore: SongCompositionMem = {
      ...songComposition,
      name: title,
      instruments: this.normalizeInstrumentList(songComposition),
      musicalPieces: (songComposition.musicalPieces ?? []).map((piece) => ({
        ...piece,
        startMeasure: Math.max(0, Number(piece.startMeasure) || 0),
        startBeat: Math.max(0, Number(piece.startBeat) || 0),
        selectedRunIndex: Math.max(0, Number(piece.selectedRunIndex) || 0),
        midiRuns: piece.midiRuns ?? [],
        measures: piece.measures ?? []
      })),
      pieceRenderings: songComposition.pieceRenderings ?? [],
      updatedAt: now,
      createdAt: songComposition.createdAt ?? now
    };
    const nextRecord: UserSongCompositionRecord = this.normalizeSongCompositionRecord({
      id: recordId,
      ownerUid: uid,
      title,
      titleLower: title.toLowerCase(),
      updatedAt: now,
      songComposition: compositionToStore
    });

    await update(ref(db, this.getUserAppPath(uid)), {
      exists: true,
      createdAt: userAppRecord?.createdAt ?? now,
      updatedAt: now
    });

    const firebaseRecord = this.removeUndefinedValues(nextRecord) as UserSongCompositionRecord;
    await set(ref(db, this.getSongCompositionRecordPath(recordId)), firebaseRecord);
    await set(ref(db, this.getSongCompositionRefPath(uid, recordId)), true);

    const compositions = await this.getRemoteSongCompositions(uid);
    this.saveLocalSongCompositions(uid, compositions);

    return nextRecord;
  }

  async deleteSongComposition(uid: string, compositionId: string): Promise<void> {
    const db = this.getDatabaseOrThrow();
    const record = await this.getSongCompositionRecord(compositionId);

    if (!record || record.ownerUid !== uid) {
      throw new Error('Song composition not found.');
    }

    await Promise.all([
      remove(ref(db, this.getSongCompositionRecordPath(compositionId))),
      remove(ref(db, this.getSongCompositionRefPath(uid, compositionId)))
    ]);

    const compositions = await this.getRemoteSongCompositions(uid);
    this.saveLocalSongCompositions(uid, compositions);
  }

  private async ensureUserRecord(user: User, displayName?: string): Promise<void> {
    const db = this.getDatabaseOrThrow();
    const now = new Date().toISOString();
    const nextSharedRecord: SharedUserRecord = {
      uid: user.uid,
      email: user.email ?? '',
      displayName: displayName || user.displayName || '',
      createdAt: now,
      updatedAt: now
    };

    await Promise.all([
      update(ref(db, this.getSharedUserPath(user.uid)), nextSharedRecord),
      update(ref(db, this.getUserAppPath(user.uid)), {
        exists: true,
        createdAt: now,
        updatedAt: now
      })
    ]);
  }

  private async getUserAppRecord(uid: string): Promise<UserAppRecord | null> {
    const db = this.getDatabaseOrThrow();
    const snapshot = await get(ref(db, this.getUserAppPath(uid)));
    return snapshot.exists() ? (snapshot.val() as UserAppRecord) : null;
  }

  private getAuthOrThrow(): Auth {
    if (!this.auth) {
      throw new Error('Firebase Auth is not configured. Add your Firebase keys in src/environments/environment.ts.');
    }
    return this.auth;
  }

  private getDatabaseOrThrow(): Database {
    if (!this.db) {
      throw new Error('Firebase Realtime Database is not configured. Add your Firebase keys in src/environments/environment.ts.');
    }
    return this.db;
  }

  private createId(): string {
    return `composition-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }

  private createSongCompositionId(): string {
    return `song-composition-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }

  private createShareId(): string {
    return `shr-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }

  private getSharedUserPath(uid: string): string {
    return `user_${this.platform}/${uid}`;
  }

  private getUserAppPath(uid: string): string {
    return `user_${this.platform}/${uid}/${this.appName}`;
  }

  private getCompositionRefsPath(uid: string): string {
    return `${this.getUserAppPath(uid)}/compositionRefs`;
  }

  private getCompositionRefPath(uid: string, compositionId: string): string {
    return `${this.getCompositionRefsPath(uid)}/${compositionId}`;
  }

  private getCompositionRecordPath(compositionId: string): string {
    return `compositions/${compositionId}`;
  }

  private getSongCompositionRefsPath(uid: string): string {
    return `${this.getUserAppPath(uid)}/songCompositionRefs`;
  }

  private getSongCompositionRefPath(uid: string, compositionId: string): string {
    return `${this.getSongCompositionRefsPath(uid)}/${compositionId}`;
  }

  private getSongCompositionRecordPath(compositionId: string): string {
    return `songCompositions/${compositionId}`;
  }

  private getPublicIndexPath(compositionId: string): string {
    return `publicCompositionIndex/${compositionId}`;
  }

  private getShareIndexPath(shareId: string): string {
    return `compositionShareIndex/${shareId}`;
  }

  private async getRemoteCompositions(uid: string): Promise<UserCompositionRecord[]> {
    const db = this.getDatabaseOrThrow();
    const refsSnapshot = await get(ref(db, this.getCompositionRefsPath(uid)));
    if (!refsSnapshot.exists()) {
      return [];
    }

    const refMap = refsSnapshot.val() as Record<string, true>;
    const compositionIds = Object.keys(refMap ?? {});
    const compositions = await Promise.all(
      compositionIds.map((compositionId) => this.getCompositionRecord(compositionId))
    );

    return this.sortCompositions(
      compositions.filter((composition): composition is UserCompositionRecord => Boolean(composition))
    );
  }

  private async getCompositionRecord(compositionId: string): Promise<UserCompositionRecord | null> {
    const db = this.getDatabaseOrThrow();
    const snapshot = await get(ref(db, this.getCompositionRecordPath(compositionId)));
    if (!snapshot.exists()) {
      return null;
    }
    return this.normalizeCompositionRecord(snapshot.val() as UserCompositionRecord);
  }

  private async getRemoteSongCompositions(uid: string): Promise<UserSongCompositionRecord[]> {
    const db = this.getDatabaseOrThrow();
    const refsSnapshot = await get(ref(db, this.getSongCompositionRefsPath(uid)));
    if (!refsSnapshot.exists()) {
      return [];
    }

    const refMap = refsSnapshot.val() as Record<string, true>;
    const compositionIds = Object.keys(refMap ?? {});
    const compositions = await Promise.all(
      compositionIds.map((compositionId) => this.getSongCompositionRecord(compositionId))
    );

    return this.sortSongCompositions(
      compositions.filter((composition): composition is UserSongCompositionRecord => Boolean(composition))
    );
  }

  private async getSongCompositionRecord(compositionId: string): Promise<UserSongCompositionRecord | null> {
    const db = this.getDatabaseOrThrow();
    const snapshot = await get(ref(db, this.getSongCompositionRecordPath(compositionId)));
    if (!snapshot.exists()) {
      return null;
    }
    return this.normalizeSongCompositionRecord(snapshot.val() as UserSongCompositionRecord);
  }

  private async syncPublicIndex(db: Database, composition: UserCompositionRecord): Promise<void> {
    if (composition.visibility === 'public') {
      await set(ref(db, this.getPublicIndexPath(composition.id)), {
        id: composition.id,
        shareId: composition.shareId,
        ownerUid: composition.ownerUid,
        title: composition.title,
        titleLower: composition.titleLower,
        updatedAt: composition.updatedAt,
        taalName: composition.taalData?.name ?? composition.composition.taal ?? ''
      });
      return;
    }

    await remove(ref(db, this.getPublicIndexPath(composition.id)));
  }

  private normalizeCompositionRecord(record: UserCompositionRecord): UserCompositionRecord {
    const shareId = record.shareId || record.composition?.shareId || this.createShareId();
    const visibility = this.normalizeVisibility(record.visibility ?? record.composition?.visibility);
    const pendingShareEmails = this.normalizeStringMap(
      record.pendingShareEmails,
      record.composition?.pendingShareEmails
    );
    const pendingSharePhones = this.normalizeStringMap(
      record.pendingSharePhones,
      record.composition?.pendingSharePhones
    );
    const aclUid = this.normalizeTrueMap(record.aclUid, record.composition?.aclUid);
    const title = record.title?.trim() || record.composition?.title?.trim() || 'Untitled Composition';
    const taalData = record.taalData ?? record.composition?.taalData ?? this.getTaalSnapshot(record.composition?.taal);

    return {
      ...record,
      shareId,
      title,
      titleLower: record.titleLower || title.toLowerCase(),
      visibility,
      pendingShareEmails,
      pendingSharePhones,
      aclUid,
      taalData,
      comments: record.comments ?? {},
      likes: record.likes ?? {},
      composition: {
        ...record.composition,
        shareId,
        visibility,
        pendingShareEmails: Object.keys(pendingShareEmails),
        pendingSharePhones: Object.keys(pendingSharePhones),
        aclUid,
        taalData
      }
    };
  }

  private normalizeSongCompositionRecord(record: UserSongCompositionRecord): UserSongCompositionRecord {
    const songComposition = this.normalizeSongComposition(record.songComposition);
    const title = record.title?.trim() || songComposition.name || 'Untitled Song Composition';

    return {
      ...record,
      title,
      titleLower: record.titleLower || title.toLowerCase(),
      updatedAt: record.updatedAt || songComposition.updatedAt || new Date().toISOString(),
      songComposition: {
        ...songComposition,
        name: title
      }
    };
  }

  private normalizeSongComposition(songComposition: SongCompositionMem): SongCompositionMem {
    const meter = Array.isArray(songComposition?.meter) && songComposition.meter.length
      ? songComposition.meter.map((value) => Number(value) || 4)
      : [4, 4];
    const pieces = (songComposition?.musicalPieces ?? []).map((piece, index) => ({
      id: piece.id || String(index + 1),
      title: piece.title || `Piece ${index + 1}`,
      startMeasure: Math.max(0, Number(piece.startMeasure) || 0),
      startBeat: Math.max(0, Number(piece.startBeat) || 0),
      targetInstrument: piece.targetInstrument || songComposition.instruments?.[0] || 'iSitar',
      selectedRunIndex: Math.max(0, Number(piece.selectedRunIndex) || 0),
      midiRuns: piece.midiRuns ?? [],
      measures: piece.measures ?? [],
      comment: piece.comment ?? ''
    }));
    const instruments = Array.from(new Set([
      ...(songComposition?.instruments ?? []),
      ...(songComposition?.initialInstruments ?? []),
      ...pieces.map((piece) => piece.targetInstrument)
    ].map((instrument) => instrument.trim()).filter(Boolean)));
    const pieceRenderings = (songComposition?.pieceRenderings ?? pieces.map((piece) => ({
      id: `rendering-${piece.id}`,
      pieceId: piece.id,
      title: piece.title,
      instrument: piece.targetInstrument,
      startMeasure: piece.startMeasure,
      measureLength: Math.max(1, piece.measures?.length ?? 1),
      enabled: true
    }))).map((rendering, index) => ({
      id: rendering.id || `rendering-${rendering.pieceId || index + 1}`,
      pieceId: rendering.pieceId || pieces[index]?.id || String(index + 1),
      title: rendering.title || pieces.find((piece) => piece.id === rendering.pieceId)?.title || `Rendering ${index + 1}`,
      instrument: rendering.instrument || pieces.find((piece) => piece.id === rendering.pieceId)?.targetInstrument || instruments[0] || 'iSitar',
      startMeasure: Math.max(0, Number(rendering.startMeasure) || 0),
      measureLength: Math.max(1, Number(rendering.measureLength) || 1),
      enabled: rendering.enabled !== false
    }));

    return {
      name: songComposition?.name?.trim() || 'Untitled Song Composition',
      defaultTempo: Number(songComposition?.defaultTempo) || 120,
      scale: songComposition?.scale || 'C',
      scaleOctave: Number(songComposition?.scaleOctave) || 4,
      meter,
      taal: songComposition?.taal ?? null,
      raaga: songComposition?.raaga ?? null,
      beatsPerMeasure: Number(songComposition?.beatsPerMeasure) || meter[0] || 4,
      instruments: instruments.length ? instruments : ['iSitar'],
      initialInstruments: songComposition?.initialInstruments ?? [],
      musicalPieces: pieces,
      pieceRenderings,
      idCounter: Number(songComposition?.idCounter) || pieces.length,
      createdAt: songComposition?.createdAt,
      updatedAt: songComposition?.updatedAt
    };
  }

  private normalizeInstrumentList(songComposition: SongCompositionMem): string[] {
    return Array.from(new Set([
      ...(songComposition.instruments ?? []),
      ...(songComposition.initialInstruments ?? []),
      ...(songComposition.musicalPieces ?? []).map((piece) => piece.targetInstrument)
    ].map((instrument) => instrument.trim()).filter(Boolean)));
  }

  private removeUndefinedValues(value: unknown): unknown {
    if (Array.isArray(value)) {
      return value.map((item) => item === undefined ? null : this.removeUndefinedValues(item));
    }

    if (value && typeof value === 'object') {
      return Object.fromEntries(
        Object.entries(value)
          .filter(([, item]) => item !== undefined)
          .map(([key, item]) => [key, this.removeUndefinedValues(item)])
      );
    }

    return value;
  }

  private normalizeVisibility(value?: string | null): CompositionVisibility {
    switch (value) {
      case 'public':
      case 'unlisted':
      case 'shared':
        return value;
      case 'private':
      default:
        return 'private';
    }
  }

  private normalizeStringMap(
    existing?: Record<string, true> | null,
    list?: string[] | null
  ): Record<string, true> {
    const result: Record<string, true> = { ...(existing ?? {}) };
    (list ?? [])
      .map((item) => item.trim())
      .filter(Boolean)
      .forEach((item) => {
        result[item] = true;
      });
    return result;
  }

  private normalizeStringMapFromList(
    list?: string[] | null,
    existing?: Record<string, true> | null
  ): Record<string, true> {
    return this.normalizeStringMap(existing, list);
  }

  private normalizeTrueMap(
    primary?: Record<string, true> | null,
    secondary?: Record<string, true> | null,
    forceUid?: string
  ): Record<string, true> {
    const result: Record<string, true> = {};

    Object.keys(primary ?? {}).forEach((key) => {
      if (key) {
        result[key] = true;
      }
    });

    Object.keys(secondary ?? {}).forEach((key) => {
      if (key) {
        result[key] = true;
      }
    });

    if (forceUid) {
      result[forceUid] = true;
    }

    return result;
  }

  private getTaalSnapshot(taalName?: string | null): TaalSnapshot | null {
    if (!taalName) {
      return null;
    }

    const taal = TaalStructure.find((item) => item.name.toLowerCase() === taalName.toLowerCase());
    if (!taal) {
      return null;
    }

    return {
      name: taal.name,
      beats: taal.beats,
      bols: [...taal.bols],
      vibhags: taal.vibhags.map((vibhag) => ({
        label: vibhag.label,
        beats: [...vibhag.beats]
      }))
    };
  }

  private sortCompositions(compositions: UserCompositionRecord[]): UserCompositionRecord[] {
    return [...compositions]
      .map((composition) => this.normalizeCompositionRecord(composition))
      .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt));
  }

  private sortSongCompositions(compositions: UserSongCompositionRecord[]): UserSongCompositionRecord[] {
    return [...compositions]
      .map((composition) => this.normalizeSongCompositionRecord(composition))
      .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt));
  }

  private getLocalCompositionsKey(uid: string): string {
    return `${this.localCachePrefix}:${uid}`;
  }

  private getLocalSongCompositionsKey(uid: string): string {
    return `${this.localSongCachePrefix}:${uid}`;
  }

  private saveLocalCompositions(uid: string, compositions: UserCompositionRecord[]): void {
    if (typeof localStorage === 'undefined') {
      return;
    }

    localStorage.setItem(this.getLocalCompositionsKey(uid), JSON.stringify(this.sortCompositions(compositions)));
    localStorage.setItem(this.localCacheLastUidKey, uid);
  }

  private listLocalCompositions(uid: string): UserCompositionRecord[] {
    if (typeof localStorage === 'undefined') {
      return [];
    }

    const raw = localStorage.getItem(this.getLocalCompositionsKey(uid));
    if (!raw) {
      return [];
    }

    try {
      const parsed = JSON.parse(raw) as UserCompositionRecord[];
      return Array.isArray(parsed) ? this.sortCompositions(parsed) : [];
    } catch {
      return [];
    }
  }

  private saveLocalSongCompositions(uid: string, compositions: UserSongCompositionRecord[]): void {
    if (typeof localStorage === 'undefined') {
      return;
    }

    localStorage.setItem(this.getLocalSongCompositionsKey(uid), JSON.stringify(this.sortSongCompositions(compositions)));
    localStorage.setItem(this.localCacheLastUidKey, uid);
  }

  private listLocalSongCompositions(uid: string): UserSongCompositionRecord[] {
    if (typeof localStorage === 'undefined') {
      return [];
    }

    const raw = localStorage.getItem(this.getLocalSongCompositionsKey(uid));
    if (!raw) {
      return [];
    }

    try {
      const parsed = JSON.parse(raw) as UserSongCompositionRecord[];
      return Array.isArray(parsed) ? this.sortSongCompositions(parsed) : [];
    } catch {
      return [];
    }
  }

  private getLastCachedUid(): string | null {
    if (typeof localStorage === 'undefined') {
      return null;
    }

    return localStorage.getItem(this.localCacheLastUidKey);
  }

  private async initializeAnalytics(): Promise<void> {
    if (!this.app || typeof window === 'undefined') {
      return;
    }

    try {
      if (await isAnalyticsSupported()) {
        this.analytics = getAnalytics(this.app);
      }
    } catch (error) {
      console.warn('Firebase Analytics initialization skipped.', error);
    }
  }

  private markAuthReady(): void {
    if (this.authInitialized) {
      return;
    }
    this.authInitialized = true;
    this.authReadyResolver?.();
    this.authReadyResolver = null;
  }
}
